/**
 * The grouped multi-link section on the task and work report detail
 * pages (ME3a).
 *
 * Replaces the one-row-per-type list from D4c1, which could only ever
 * show a single link per type because that is all the legacy columns
 * held. It keeps that block's visual language exactly — label, icon,
 * amber pill for a real destination, muted pill for text — and adds the
 * two things multi-links need:
 *
 * * **Several values per type**, each its own pill.
 * * **Provenance**, in words rather than in colour alone: "Manuell",
 *   "Automatisch über Mietverhältnisse", or both when a link was picked
 *   AND derived. A link is listed ONCE regardless — showing it twice
 *   would read as two links.
 *
 * Never renders a ``link_id``, a source id, or an entity id. A link
 * whose label the server could not resolve shows a neutral phrase, so a
 * deleted or invisible row degrades to words instead of a UUID.
 *
 * EVERY type gets a row, including the empty ones, which read "Nicht
 * verknüpft". That is the D4c1 decision this block inherits and keeps:
 * the section answers the question completely, and a missing row would
 * read as a rendering bug rather than as an absent link.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FileText } from "lucide-react";

import { LINK_ICONS } from "./EntityLinkRows";
import {
  ENTITY_LINK_TYPES,
  ENTITY_LINK_TYPES_BY_NAME,
} from "./multiEntityLinks";

/** Fallback when a type has no phrase of its own. Never the id. */
export const UNNAMED = "Nicht verfügbar";

/**
 * The provenance sentence for one link, or null when there is nothing to
 * say. Manual-only links say "Manuell"; derived ones name the type of
 * selection they came from, counted when there are several.
 */
export function provenanceText(link) {
  const sources = (link?.derived_from || [])
    .map((s) => ENTITY_LINK_TYPES_BY_NAME[s?.entity_type]?.groupLabel)
    .filter(Boolean);
  const unique = Array.from(new Set(sources));

  if (!unique.length) return link?.is_manual ? "Manuell" : null;

  const via =
    sources.length > unique.length || sources.length > 2
      ? `${sources.length} Verknüpfungen`
      : unique.join(", ");
  const automatic = `Automatisch über ${via}`;
  return link?.is_manual ? `Manuell · zusätzlich ${automatic.toLowerCase()}` : automatic;
}

/**
 * One entity type's links, or null when it has none.
 *
 * Falls back to the legacy singular field when the array is ABSENT — a
 * response written before ME2a still answers with ``tenant_id`` alone,
 * and a detail page that showed nothing for it would read as "this task
 * is unlinked" when it plainly is not. An array that is present but
 * EMPTY is honoured as-is: that means no links, and reviving a stale
 * singular id there would show one the user removed.
 */
export function linkGroupsFor(row) {
  return ENTITY_LINK_TYPES.map((d) => {
    const array = row?.[d.arrayField];
    const links = Array.isArray(array)
      ? array
          .filter((link) => link && link.id)
          .map((link) => ({
            id: String(link.id),
            value: (link.label || "").trim() || d.unavailableLabel || UNNAMED,
            to: d.detailPath(String(link.id)) || null,
            provenance: provenanceText(link),
          }))
      : legacyLinksFor(row, d);
    return { key: d.type, label: d.groupLabel, links };
  });
}

/** The single legacy link of one type, as a one-element group. */
function legacyLinksFor(row, d) {
  const id = row?.[d.singularField];
  if (!id) return [];
  return [
    {
      id: String(id),
      value: (d.legacyLabel?.(row) || "").trim() || d.unavailableLabel || UNNAMED,
      to: d.detailPath(String(id)) || null,
      // Pre-ME2a rows carry no provenance; every link there was a
      // deliberate choice, so saying "Manuell" is accurate.
      provenance: "Manuell",
    },
  ];
}

function LinkPill({ link }) {
  const body = <span className="truncate">{link.value}</span>;
  return link.to ? (
    <Link
      to={link.to}
      className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-[8px] border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[12px] font-medium text-amber-700 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-400"
    >
      {body}
      <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    </Link>
  ) : (
    <span className="inline-flex min-w-0 max-w-full rounded-[8px] border border-border bg-muted/60 px-2.5 py-1 text-[12px] font-medium text-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0]">
      {body}
    </span>
  );
}

export default function EntityLinkGroups({ row }) {
  const groups = linkGroupsFor(row);

  return (
    <div>
      {groups.map((group) => {
        const Icon = LINK_ICONS[group.key] || FileText;
        return (
          <div
            key={group.key}
            className="flex flex-col gap-1.5 border-b border-border/60 py-2 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-3 dark:border-[#1e2130]/70"
          >
            <span className="flex shrink-0 items-center gap-2 text-[12px] text-muted-foreground dark:text-[#4a5070]">
              <Icon
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              {group.label}
            </span>
            {group.links.length === 0 ? (
              <span className="text-[12px] text-muted-foreground dark:text-[#4a5070]">
                Nicht verknüpft
              </span>
            ) : (
            <ul className="flex min-w-0 list-none flex-col items-start gap-1.5 p-0 sm:items-end">
              {group.links.map((link) => (
                <li key={link.id} className="flex max-w-full flex-col items-start gap-0.5 sm:items-end">
                  <LinkPill link={link} />
                  {link.provenance ? (
                    <span className="text-[11px] text-muted-foreground dark:text-[#4a5070]">
                      {link.provenance}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
